import { useState } from 'react';
import { EditSubscriptionOverridesDrawer } from './EditSubscriptionOverridesDrawer';
import { useResendInvite } from '@/hooks/useResendInvite';
import {
  useAccountTenantSubscription,
  useAccountTenants,
  useMakeAccountTenantPrimary,
} from '@/hooks/useTenantSubscription';
import type { CRMAccountTenant } from '@/types/api';

type AccountTenantPanelProps = {
  accountId: string;
  onCreateTenant: () => void;
};

function formatAmount(amountCents?: number | null) {
  if (amountCents == null) {
    return 'Plan default';
  }
  return `$${(amountCents / 100).toFixed(2)}`;
}

function TenantActions({
  accountId,
  tenant,
  onEdit,
}: {
  accountId: string;
  tenant: CRMAccountTenant;
  onEdit: (tenantId: string) => void;
}) {
  const resend = useResendInvite(accountId, tenant.tenant_id);
  const makePrimary = useMakeAccountTenantPrimary(accountId);

  return (
    <div className="entity-header-actions">
      {!tenant.is_primary ? (
        <button
          type="button"
          className="button button-secondary"
          onClick={() => makePrimary.mutate(tenant.tenant_id)}
          disabled={makePrimary.isPending}
        >
          {makePrimary.isPending ? 'Saving...' : 'Make primary'}
        </button>
      ) : null}
      <button
        type="button"
        className="button button-secondary"
        onClick={() => resend.mutate(undefined)}
        disabled={resend.isPending}
      >
        {resend.isPending ? 'Resending...' : 'Resend invite'}
      </button>
      <button type="button" className="button button-primary" onClick={() => onEdit(tenant.tenant_id)}>
        Edit subscription
      </button>
    </div>
  );
}

export function AccountTenantPanel({ accountId, onCreateTenant }: AccountTenantPanelProps) {
  const tenants = useAccountTenants(accountId);
  const [editingTenantId, setEditingTenantId] = useState<string | null>(null);
  const selectedSubscription = useAccountTenantSubscription(accountId, editingTenantId ?? undefined);

  if (tenants.isLoading) {
    return <div className="loading-row">Loading customer tenants...</div>;
  }

  if (tenants.isError || !tenants.data) {
    return <p className="form-error">Unable to load customer tenants.</p>;
  }

  const canCreate = tenants.data.can_create_additional_tenants || tenants.data.total === 0;

  return (
    <div className="detail-stack">
      <div className="call-session-banner">
        <div>
          <p className="entity-breadcrumb">Customer Tenants</p>
          <h3>{tenants.data.total} linked tenant{tenants.data.total === 1 ? '' : 's'}</h3>
          <p>
            {tenants.data.multi_tenant_feature_enabled
              ? 'Enterprise multi-tenant creation is enabled for this account.'
              : tenants.data.total > 0
                ? 'Additional tenants require the enterprise multi-tenant feature.'
                : 'Create the first tenant to promote this account into the AP product.'}
          </p>
        </div>
        <button
          type="button"
          className="button button-primary"
          onClick={onCreateTenant}
          disabled={!canCreate}
        >
          Create tenant
        </button>
      </div>

      {tenants.data.items.length ? (
        <div className="timeline-list">
          {tenants.data.items.map((tenant) => (
            <article key={tenant.id} className="timeline-item">
              <div className="timeline-meta">
                <div>
                  <strong>{tenant.display_name || tenant.tenant_name}</strong>
                  <p>
                    <span className="mono-inline">{tenant.tenant_slug}</span> · {tenant.tenant_status}
                  </p>
                </div>
                <div className="entity-header-actions">
                  {tenant.is_primary ? <span className="pill pill-medium">Primary</span> : null}
                  <span className="pill pill-soft">{tenant.status}</span>
                </div>
              </div>

              <div className="entity-meta-grid">
                <article className="entity-meta-card">
                  <span>Plan</span>
                  <strong>{tenant.subscription?.plan_display_name ?? 'Unassigned'}</strong>
                </article>
                <article className="entity-meta-card">
                  <span>Allocation</span>
                  <strong>{formatAmount(tenant.subscription?.custom_amount_cents)}</strong>
                </article>
                <article className="entity-meta-card">
                  <span>Admin</span>
                  <strong>{tenant.admin_email ?? 'No admin found'}</strong>
                </article>
                <article className="entity-meta-card">
                  <span>Invite</span>
                  <strong>{tenant.invite_pending ? 'Pending' : 'Accepted'}</strong>
                </article>
              </div>

              <TenantActions accountId={accountId} tenant={tenant} onEdit={setEditingTenantId} />
            </article>
          ))}
        </div>
      ) : (
        <p className="muted-copy">No product tenant has been created for this account yet.</p>
      )}

      {editingTenantId && selectedSubscription.isLoading ? (
        <div className="loading-row">Loading tenant subscription...</div>
      ) : null}

      {editingTenantId && selectedSubscription.data ? (
        <EditSubscriptionOverridesDrawer
          accountId={accountId}
          tenantId={editingTenantId}
          open={Boolean(editingTenantId)}
          onClose={() => setEditingTenantId(null)}
          initial={selectedSubscription.data}
        />
      ) : null}
    </div>
  );
}
